import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Audit } from '@vsolv/core/audit/domain';
import { PaginationQueryRequest } from '@vsolv/dev-kit/nest';
import { Documents } from '@vsolv/packages/documents/domain';
import { Note } from '@vsolv/packages/notes/domain';
import { StorageItem } from '@vsolv/packages/storage/domain';
import { Claim, ClaimLifecycleStep } from '@wsphere/warranties/domain';
import moment from 'moment';
import { BehaviorSubject, catchError, firstValueFrom, Observable, of, Subject } from 'rxjs';

/** The claims queue filters, as query params. */
function queueParams(data: Claim.ListClaimsQueryRequest) {
  return {
    ...(data.minAgeDays !== undefined && { minAgeDays: data.minAgeDays }),
    ...(data.hasOpenHold !== undefined && { hasOpenHold: data.hasOpenHold }),
    ...(data.holdDueBy && { holdDueBy: moment(data.holdDueBy).format('YYYY-MM-DD') }),
    ...(data.hasOpenEscalation !== undefined && { hasOpenEscalation: data.hasOpenEscalation }),
    ...(data.authorizationStates?.length && { authorizationStates: data.authorizationStates }),
    ...(data.paymentReadiness?.length && { paymentReadiness: data.paymentReadiness }),
    ...(data.contactReasons?.length && { contactReasons: data.contactReasons }),
    ...(data.overrideStates?.length && { overrideStates: data.overrideStates }),
    ...(data.repairDeadlineBy && { repairDeadlineBy: moment(data.repairDeadlineBy).format('YYYY-MM-DD') }),
  };
}

@Injectable()
export class ClaimService {
  private http = inject(HttpClient);

  private runningEligibilityChecks = new Map<
    string,
    Promise<Claim.ClaimItem.Eligibility.EvaluateEligibilityResponse>
  >();

  private claim$ = new BehaviorSubject<Claim.Model | null>(null);
  private claimNotes$ = new BehaviorSubject<Note.Model[] | null>(null);

  private documentsChanged = new Subject<string>();
  /** The claim id each time a file is uploaded or attached to it, so lists left on screen can reload. */
  readonly documentsChanged$ = this.documentsChanged.asObservable();

  list(data: Claim.ListClaimsQueryRequest) {
    return firstValueFrom(
      this.http.get<Claim.ListClaimsQueryResponse>(`/api/claims`, {
        params: {
          ...(data.page && { page: data.page }),
          ...(data.limit && { limit: data.limit }),
          ...(data.search && { search: data.search }),
          ...(data.statuses && { statuses: data.statuses }),
          ...(data.warrantyId && { warrantyId: data.warrantyId }),
          ...(data.coverageId && { coverageId: data.coverageId }),
          ...(data.customerId && { customerId: data.customerId }),
          ...(data.claimNumber && { claimNumber: data.claimNumber }),
          ...(data.distributorId && { distributorId: data.distributorId }),
          ...(data.claimItemStatuses && { claimItemStatuses: data.claimItemStatuses }),
          ...(data.claimPaymentStatuses && { claimPaymentStatuses: data.claimPaymentStatuses }),
          ...(data.claimLifecycleSteps && { claimLifecycleSteps: data.claimLifecycleSteps }),
          ...(data.resolutions && { resolutions: data.resolutions }),
          ...queueParams(data),
          ...(data.sortBy && { sortBy: data.sortBy }),
          ...(data.sortOrder && { sortOrder: data.sortOrder }),
        },
      })
    );
  }

  list$(data: Claim.ListClaimsQueryRequest): Observable<Claim.ListClaimsQueryResponse> {
    return this.http.get<Claim.ListClaimsQueryResponse>(`/api/claims`, {
      params: {
        ...(data.page && { page: data.page }),
        ...(data.limit && { limit: data.limit }),
        ...(data.search && { search: data.search }),
        ...(data.statuses && { statuses: data.statuses }),
        ...(data.warrantyId && { warrantyId: data.warrantyId }),
        ...(data.coverageId && { coverageId: data.coverageId }),
        ...(data.customerId && { customerId: data.customerId }),
        ...(data.claimNumber && { claimNumber: data.claimNumber }),
        ...(data.distributorId && { distributorId: data.distributorId }),
        ...(data.claimItemStatuses && { claimItemStatuses: data.claimItemStatuses }),
        ...(data.claimPaymentStatuses && { claimPaymentStatuses: data.claimPaymentStatuses }),
        ...(data.claimLifecycleSteps && { claimLifecycleSteps: data.claimLifecycleSteps }),
        ...(data.resolutions && { resolutions: data.resolutions }),
        ...queueParams(data),
        ...(data.sortBy && { sortBy: data.sortBy }),
        ...(data.sortOrder && { sortOrder: data.sortOrder }),
      },
    });
  }

  async get(claimId: string) {
    return await firstValueFrom(
      this.http.get<Claim.Model>(`/api/claims/${claimId}`).pipe(
        catchError(err => {
          if (err.status === 404) return of(null);
          throw err;
        })
      )
    );
  }

  open(data: Claim.OpenClaimRequest) {
    return firstValueFrom(
      this.http.post<Claim.OpenClaimResponse>(`/api/claims`, {
        warrantyId: data.warrantyId,
        complaint: data.complaint,
        allowExpiredOrCancelledWarranty: false,
        notes: data.notes,
        ...(data.items && { items: data.items }),
        ...(data.lossDate !== undefined && { lossDate: data.lossDate }),
        ...(data.dateReported !== undefined && { dateReported: data.dateReported }),
      })
    );
  }

  /** Partial: send only the fields that changed. */
  update(claimId: string, data: Claim.UpdateClaimRequest) {
    return firstValueFrom(this.http.patch<Claim.UpdateClaimResponse>(`/api/claims/${claimId}`, data));
  }

  authorize(claimId: string, data: Claim.Authorization.AuthorizeClaimRequest) {
    return firstValueFrom(
      this.http.post<Claim.Authorization.AuthorizeClaimResponse>(`/api/claims/${claimId}/authorizations`, data)
    );
  }

  listAllocations(claimId: string) {
    return firstValueFrom(
      this.http.get<Claim.Allocation.ListAllocationsResponse>(`/api/claims/${claimId}/allocations`)
    );
  }

  listOverrides(claimId: string) {
    return firstValueFrom(this.http.get<Claim.Override.ListOverridesResponse>(`/api/claims/${claimId}/overrides`));
  }

  decideOverride(claimId: string, overrideId: string, data: Claim.Override.DecideOverrideRequest) {
    return firstValueFrom(
      this.http.post<Claim.Override.Model>(`/api/claims/${claimId}/overrides/${overrideId}/decision`, data)
    );
  }

  splitAllocations(claimId: string, data: Claim.Allocation.SplitAllocationsRequest) {
    return firstValueFrom(
      this.http.put<Claim.Allocation.SplitAllocationsResponse>(`/api/claims/${claimId}/allocations`, data)
    );
  }

  reverseAuthorization(
    claimId: string,
    authorizationId: string,
    data: Claim.Authorization.ReverseClaimAuthorizationRequest
  ) {
    return firstValueFrom(
      this.http.post<Claim.Authorization.ReverseClaimAuthorizationResponse>(
        `/api/claims/${claimId}/authorizations/${authorizationId}/reverse`,
        data
      )
    );
  }

  updateResolution(claimId: string, data: Claim.UpdateClaimResolutionRequest) {
    return firstValueFrom(
      this.http.patch<Claim.UpdateClaimResolutionResponse>(`/api/claims/${claimId}/resolution`, {
        resolution: data.resolution,
      })
    );
  }

  updateContactReason(claimId: string, data: Claim.UpdateClaimContactReasonRequest) {
    return firstValueFrom(
      this.http.patch<Claim.UpdateClaimContactReasonResponse>(`/api/claims/${claimId}/contact-reason`, {
        contactReason: data.contactReason,
      })
    );
  }

  updateClaimLifecycleSteps(claimId: string, data: ClaimLifecycleStep.UpdateClaimLifecycleStepsRequest) {
    return firstValueFrom(
      this.http.patch<ClaimLifecycleStep.UpdateClaimLifecycleStepsResponse>(
        `/api/claims/${claimId}/claim-lifecycle-steps`,
        data
      )
    );
  }

  expire(claimId: string) {
    return firstValueFrom(this.http.patch<Claim.ExpireClaimResponse>(`/api/claims/${claimId}/expire`, {}));
  }

  cancel(claimId: string, cancellationReason = '') {
    return firstValueFrom(
      this.http.patch<Claim.CancelClaimResponse>(`/api/claims/${claimId}/cancel`, {
        cancellationReason,
      })
    );
  }

  close(claimId: string) {
    return firstValueFrom(this.http.patch<Claim.ExpireClaimResponse>(`/api/claims/${claimId}/close`, {}));
  }

  listItems(claimId: string, data?: Claim.ClaimItem.ListClaimItemsQueryRequest) {
    return firstValueFrom(
      this.http.get<Claim.ClaimItem.ListClaimItemsQueryResponse>(`/api/claims/${claimId}/items`, {
        params: {
          ...(data?.page && { page: data.page }),
          ...(data?.limit && { limit: data.limit }),
          ...(data?.status && { status: data.status }),
          ...(data?.search && { search: data.search }),
          ...(data?.withDeleted && { withDeleted: data.withDeleted }),
        },
      })
    );
  }

  listItems$(
    claimId: string,
    data?: Claim.ClaimItem.ListClaimItemsQueryRequest
  ): Observable<Claim.ClaimItem.ListClaimItemsQueryResponse> {
    return this.http.get<Claim.ClaimItem.ListClaimItemsQueryResponse>(`/api/claims/${claimId}/items`, {
      params: {
        ...(data?.page && { page: data.page }),
        ...(data?.limit && { limit: data.limit }),
        ...(data?.status && { status: data.status }),
        ...(data?.search && { search: data.search }),
        ...(data?.withDeleted && { withDeleted: data.withDeleted }),
      },
    });
  }

  getItem(claimId: string, itemId: string) {
    return firstValueFrom(
      this.http.get<Claim.ClaimItem.Model>(`/api/claims/${claimId}/items/${itemId}`).pipe(
        catchError(err => {
          if (err.status === 404) return of(null);
          throw err;
        })
      )
    );
  }

  createItem(claimId: string, data: Claim.ClaimItem.CreateClaimItemRequest) {
    return firstValueFrom(
      this.http.post<Claim.ClaimItem.CreateClaimItemResponse>(`/api/claims/${claimId}/items`, {
        ...(data.description && { description: data.description }),
        ...(data.coverageId ? { coverageId: data.coverageId } : { coverageId: null }),
        ...(data.dateOfPurchase ? { dateOfPurchase: data.dateOfPurchase } : { dateOfPurchase: null }),
      })
    );
  }

  updateItem(claimId: string, itemId: string, data: Claim.ClaimItem.UpdateClaimItemRequest) {
    return firstValueFrom(
      this.http.patch<Claim.ClaimItem.UpdateClaimItemResponse>(`/api/claims/${claimId}/items/${itemId}`, {
        ...(data.cure && { cure: data.cure }),
        ...(data.amount && { amount: data.amount }),
        ...(data.resolution && { resolution: data.resolution }),
        ...(data.coverageId && { coverageId: data.coverageId }),
        ...(data.description && { description: data.description }),
        ...(data.dateOfPurchase && { dateOfPurchase: data.dateOfPurchase }),
      })
    );
  }

  approveItem(claimId: string, itemId: string, reason: string) {
    return firstValueFrom(
      this.http.patch<Claim.ClaimItem.ApproveClaimItemResponse>(`/api/claims/${claimId}/items/${itemId}/approve`, {
        reason,
      })
    );
  }

  rejectItem(claimId: string, itemId: string, reason: string) {
    return firstValueFrom(
      this.http.patch<Claim.ClaimItem.ApproveClaimItemResponse>(`/api/claims/${claimId}/items/${itemId}/reject`, {
        reason,
      })
    );
  }

  /** Approve, reject or redraft with the full decision body (reason code, amounts, version). */
  decideItem(
    claimId: string,
    itemId: string,
    action: 'approve' | 'reject' | 'redraft',
    body:
      | Claim.ClaimItem.ApproveClaimItemRequest
      | Claim.ClaimItem.RejectClaimItemRequest
      | Claim.ClaimItem.RedraftClaimItemRequest
  ) {
    return firstValueFrom(
      this.http.patch<Claim.ClaimItem.ApproveClaimItemResponse>(
        `/api/claims/${claimId}/items/${itemId}/${action}`,
        body
      )
    );
  }

  listItemDecisions(claimId: string, itemId: string) {
    return firstValueFrom(
      this.http.get<Claim.ClaimItem.Decision.ListDecisionsResponse>(`/api/claims/${claimId}/items/${itemId}/decisions`)
    );
  }

  listItemEligibility(claimId: string, itemId: string) {
    return firstValueFrom(
      this.http.get<Claim.ClaimItem.Eligibility.ListEligibilityResponse>(
        `/api/claims/${claimId}/items/${itemId}/eligibility`
      )
    );
  }

  checkItemEligibility(claimId: string, itemId: string) {
    // Two cards checking the same job at once share one request, so they don't add two versions.
    const running = this.runningEligibilityChecks.get(itemId);
    if (running) return running;

    const check = firstValueFrom(
      this.http.post<Claim.ClaimItem.Eligibility.EvaluateEligibilityResponse>(
        `/api/claims/${claimId}/items/${itemId}/eligibility`,
        {}
      )
    ).finally(() => this.runningEligibilityChecks.delete(itemId));

    this.runningEligibilityChecks.set(itemId, check);
    return check;
  }

  getDecisionReasons() {
    return firstValueFrom(
      this.http.get<Claim.ClaimItem.Decision.RetrieveDecisionReasonsResponse>(`/api/claim-item-decision-reasons`)
    );
  }

  resolveItem(claimId: string, itemId: string, data: Claim.ClaimItem.ResolveClaimItemRequest) {
    return firstValueFrom(
      this.http.patch<Claim.ClaimItem.ApproveClaimItemResponse>(`/api/claims/${claimId}/items/${itemId}/resolve`, {
        ...(data.amount && { amount: data.amount }),
        ...(data.resolution && { resolution: data.resolution }),
      })
    );
  }

  redraftItem(claimId: string, itemId: string, reason?: string | null) {
    return firstValueFrom(
      this.http.patch<Claim.ClaimItem.RedraftClaimItemResponse>(`/api/claims/${claimId}/items/${itemId}/redraft`, {
        ...(reason ? { reason } : { reason: null }),
      })
    );
  }

  removeItem(claimId: string, itemId: string, deletedReason: string) {
    return firstValueFrom(
      this.http.delete<Claim.ClaimItem.DeleteClaimItemResponse>(`/api/claims/${claimId}/items/${itemId}`, {
        params: { deletedReason },
      })
    );
  }

  listNotes(claimId: string, data?: Claim.Notes.ListClaimNotesQueryRequest) {
    return firstValueFrom(
      this.http.get<Claim.Notes.ListClaimNotesQueryResponse>(`/api/claims/${claimId}/notes`, {
        params: {
          ...(data?.page && { page: data.page }),
          ...(data?.limit && { limit: data.limit }),
          ...(data?.search && { search: data.search }),
          ...(data?.createdById && { createdById: data.createdById }),
        },
      })
    );
  }

  listNotes$(
    claimId: string,
    data?: Claim.Notes.ListClaimNotesQueryRequest
  ): Observable<Claim.Notes.ListClaimNotesQueryResponse> {
    return this.http.get<Claim.Notes.ListClaimNotesQueryResponse>(`/api/claims/${claimId}/notes`, {
      params: {
        ...(data?.page && { page: data.page }),
        ...(data?.limit && { limit: data.limit }),
        ...(data?.search && { search: data.search }),
        ...(data?.createdById && { createdById: data.createdById }),
      },
    });
  }

  createNote(claimId: string, data: Claim.Notes.CreateClaimNoteRequest) {
    return firstValueFrom(
      this.http.post<Claim.Notes.CreateClaimNoteResponse>(`/api/claims/${claimId}/notes`, {
        title: data.title,
        content: data.content,
        feed: data.feed,
      })
    );
  }

  updateNote(claimId: string, noteId: string, data: Claim.Notes.UpdateClaimNoteRequest) {
    return firstValueFrom(
      this.http.patch<Claim.Notes.UpdateClaimNoteResponse>(`/api/claims/${claimId}/notes/${noteId}`, {
        title: data.title,
        content: data.content,
        feed: data.feed,
      })
    );
  }

  deleteNote(claimId: string, noteId: string) {
    return firstValueFrom(
      this.http.delete<Claim.Notes.DeleteClaimNoteResponse>(`/api/claims/${claimId}/notes/${noteId}`)
    );
  }

  async listDocuments(claimId: string, dto?: Claim.Documents.ListClaimDocumentsRequest) {
    return await firstValueFrom(
      this.http.get<Claim.Documents.ListClaimDocumentsResponse>(`/api/claims/${claimId}/documents`, {
        params: {
          ...(dto?.page && { page: dto.page }),
          ...(dto?.limit && { limit: dto.limit }),
          ...(dto?.search && { search: dto.search }),
          ...(dto?.feed?.length && { feed: dto.feed }),
          ...(dto?.extension && { extension: dto.extension }),
        },
      })
    );
  }

  listDocuments$(
    claimId: string,
    dto?: Claim.Documents.ListClaimDocumentsRequest
  ): Observable<Claim.Documents.ListClaimDocumentsResponse> {
    return this.http.get<Claim.Documents.ListClaimDocumentsResponse>(`/api/claims/${claimId}/documents`, {
      params: {
        ...(dto?.page && { page: dto.page }),
        ...(dto?.limit && { limit: dto.limit }),
        ...(dto?.search && { search: dto.search }),
        ...(dto?.feed?.length && { feed: dto.feed }),
        ...(dto?.extension && { extension: dto.extension }),
      },
    });
  }

  async listAllDocuments(
    claimId: string,
    dto?: Omit<Claim.Documents.ListClaimDocumentsRequest, keyof PaginationQueryRequest>
  ) {
    const items: StorageItem.Model[] = [];

    let page: Claim.Documents.ListClaimDocumentsResponse | null = await this.listDocuments(claimId, {
      ...dto,
      page: 1,
    });

    while (page) {
      items.push(...page.items);

      page =
        (page.meta.totalPages ?? 0) > page.meta.currentPage
          ? await this.listDocuments(claimId, { ...dto, page: page.meta.currentPage + 1 })
          : null;
    }

    return items;
  }

  async getDocument(claimId: string, documentId: string) {
    return await firstValueFrom(
      this.http.get<Documents.RetrieveDocumentResponse>(`/api/claims/${claimId}/documents/${documentId}`)
    );
  }

  async createDocument(claimId: string, data: Claim.Documents.CreateClaimDocumentRequest) {
    const formData = new FormData();
    for (const key in data) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const value = (data as any)[key];
      if (value !== undefined && value !== null) formData.set(key, value);
    }

    const response = await firstValueFrom(
      this.http.post<StorageItem.UploadCommandResponse>(`/api/claims/${claimId}/documents`, formData)
    ).catch(err => {
      throw new Error(err.message);
    });

    this.documentsChanged.next(claimId);
    return response.id;
  }

  /** Attaches the customer's existing repository documents to the claim, reusing the stored files. */
  async linkRepositoryDocuments(claimId: string, warrantyDocumentIds: string[]) {
    const body: Claim.Documents.LinkRepositoryDocumentsRequest = { warrantyDocumentIds };
    const response = await firstValueFrom(
      this.http.post<Claim.Documents.LinkRepositoryDocumentsResponse>(
        `/api/claims/${claimId}/documents/repository`,
        body
      )
    ).catch(err => {
      throw new Error(err.message);
    });

    // Already linked documents were on the claim before, so only a new link changes its list.
    if (response.results.some(result => result.status === Claim.Documents.LinkRepositoryDocumentStatus.LINKED))
      this.documentsChanged.next(claimId);
    return response;
  }

  async updateDocument(
    claimId: string,
    documentId: string,
    dto: { title?: string; description?: string | null; visibleToCustomer?: boolean }
  ) {
    return await firstValueFrom(
      this.http.patch<Documents.UpdateDocumentResponse>(`/api/claims/${claimId}/documents/${documentId}`, {
        title: dto.title,
        description: dto.description,
        ...(dto.visibleToCustomer !== undefined && { feed: dto.visibleToCustomer ? ['staff', 'customer'] : ['staff'] }),
      })
    );
  }

  async removeDocument(claimId: string, documentId: string) {
    return await firstValueFrom(
      this.http.delete<Documents.DeleteDocumentCommandResponse>(`/api/claims/${claimId}/documents/${documentId}`)
    );
  }

  /** Every audit entry under the claim, newest first; `entityType` narrows it to one kind of record. */
  listAudit(claimId: string, data?: { page?: number; limit?: number; entityType?: string | null }) {
    return firstValueFrom(
      this.http.get<Audit.Model>(`/api/claims/${claimId}/audit`, {
        params: {
          ...(data?.page && { page: data.page }),
          ...(data?.limit && { limit: data.limit }),
          ...(data?.entityType && { entityType: data.entityType }),
        },
      })
    );
  }

  listPayments(claimId: string, data?: Claim.ClaimPayment.ListClaimPaymentsQueryRequest) {
    return firstValueFrom(
      this.http.get<Claim.ClaimPayment.ListClaimPaymentQueryResponse>(`/api/claims/${claimId}/payments`, {
        params: {
          ...(data?.page && { page: data.page }),
          ...(data?.limit && { limit: data.limit }),
          ...(data?.search && { search: data.search }),
          ...(data?.createdById && { createdById: data.createdById }),
          ...(data?.sortBy && { sortBy: data.sortBy }),
          ...(data?.sortOrder && { sortOrder: data.sortOrder }),
        },
      })
    );
  }

  async getPayment(claimId: string, paymentId: string) {
    return await firstValueFrom(
      this.http.get<Claim.ClaimPayment.RetrieveClaimPaymentQueryResponse>(
        `/api/claims/${claimId}/payments/${paymentId}`
      )
    );
  }

  createPayment(claimId: string, data: Claim.ClaimPayment.CreateClaimPaymentRequest) {
    return firstValueFrom(
      this.http.post<Claim.ClaimPayment.CreateClaimPaymentResponse>(`/api/claims/${claimId}/payments`, {
        ...(data.title && { title: data.title }),
        ...(data.description && { description: data.description }),
        ...(data.destination && { destination: data.destination }),
        ...(data.amount && { amount: data.amount }),
        ...(data.customerId && { customerId: data.customerId }),
        ...(data.authorizationId && { authorizationId: data.authorizationId }),
        ...(data.allocationId && { allocationId: data.allocationId }),
      })
    );
  }

  updatePayment(claimId: string, paymentId: string, data: Claim.ClaimPayment.UpdateClaimPaymentRequest) {
    return firstValueFrom(
      this.http.patch<Claim.ClaimPayment.UpdateClaimPaymentResponse>(`/api/claims/${claimId}/payments/${paymentId}`, {
        ...(data.title && { title: data.title }),
        ...(data.description && { description: data.description }),
        ...(data.destination && { destination: data.destination }),
        ...(data.amount && { amount: data.amount }),
        ...(data.status && { status: data.status }),
      })
    );
  }

  issuePayment(claimId: string, paymentId: string) {
    return firstValueFrom(this.http.patch<{ id: string }>(`/api/claims/${claimId}/payments/${paymentId}/issue`, {}));
  }

  voidPayment(claimId: string, paymentId: string) {
    return firstValueFrom(this.http.patch<{ id: string }>(`/api/claims/${claimId}/payments/${paymentId}/void`, {}));
  }

  getClaim() {
    return this.claim$.asObservable();
  }

  getClaimNotes() {
    return this.claimNotes$.asObservable();
  }

  async refreshClaim(claim?: Claim.Model) {
    if (this.claim$.value) {
      if (claim) {
        if (this.claim$.value.id !== claim.id) this.claim$.next(claim);
      } else {
        const id = this.claim$.value.id;
        const claim = await this.get(id);
        this.claim$.next(claim);
      }
    } else {
      if (claim) {
        this.claim$.next(claim);
      }
    }
  }

  /** Totals are recalculated in the background after a change, so re-read the claim until they move.
   * Pass `force` when the claim may be getting its first totals (its first charge). */
  async refreshClaimTotals(force = false, delaysMs = [300, 800, 1500, 3000]) {
    const current = this.claim$.value;
    if (!current || (!force && !current.totals)) return;

    const before = JSON.stringify(current.totals ?? null);

    for (const delayMs of delaysMs) {
      await new Promise(resolve => setTimeout(resolve, delayMs));
      if (this.claim$.value?.id !== current.id) return;

      const claim = await this.get(current.id).catch(() => null);
      if (!claim) return;
      if (this.claim$.value?.id !== current.id) return;

      const fresh = JSON.stringify(claim.totals ?? null);
      if (fresh !== before) {
        // A plain refreshClaim() may have already picked them up; don't redraw twice.
        if (fresh !== JSON.stringify(this.claim$.value?.totals ?? null)) this.claim$.next(claim);
        return;
      }
    }
  }

  async refreshClaimNotes(claim?: Claim.Model) {
    const claimId = claim?.id ?? this.claim$.value?.id;
    if (!claimId) return;

    if (claim && (!this.claim$.value || this.claim$.value.id !== claim.id)) {
      this.claim$.next(claim);
    }

    const notes = await this.listNotes(claimId);
    this.claimNotes$.next(notes.items);
  }

  clearClaim() {
    this.claim$.next(null);
    this.claimNotes$.next(null);
  }

  lifecycleStepsFilter() {
    return firstValueFrom(this.http.get<string[]>(`/api/lifecycle-steps-filter`));
  }
}
